/**
 * EditCollectionModal — partial-update of a collection's safe fields
 * (name, industry, region, description). Taxonomy / dimensions /
 * channels stay read-only in v1 (their changes raise orphan-data
 * questions that aren't worth the surface in this round).
 *
 * Pre-fills from the `collection` prop; submits only fields that have
 * actually changed (diff-aware payload) so unchanged values don't churn
 * server-side or trigger spurious name-collision checks.
 */

import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Input } from '../../../../../ui/input';
import { Label } from '../../../../../ui/label';
import { Textarea } from '../../../../../ui/textarea';

import { useUpdateCollection } from '../../hooks/useDatasets';
import type { Collection, UpdateCollectionPayload } from '../../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection: Collection;
}

type FieldErrors = Partial<{
  name: string;
  industry: string;
  region: string;
}>;

const EditCollectionModal: React.FC<Props> = ({ open, onOpenChange, collection }) => {
  const update = useUpdateCollection(collection.id);

  const [name, setName] = useState(collection.name);
  const [industry, setIndustry] = useState(collection.industry);
  const [region, setRegion] = useState(collection.region);
  const [description, setDescription] = useState(collection.description ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});

  // Snapshot the collection's values at the moment the modal opens.
  // The diff in `handleSubmit` compares against this snapshot, not
  // against the live `collection` prop, so a background TanStack-Query
  // refetch doesn't wipe the user's in-flight edits while the modal is
  // open.
  const snapshotRef = useRef(collection);
  // Intentionally depend only on `open`, not `collection`: re-seeding on
  // every `collection` ref change is the exact bug we're fixing (see
  // MJ-3 in the code review — background TanStack-Query refetches were
  // wiping user edits). The latest `collection` is captured at the
  // moment `open` flips true.
  useEffect(() => {
    if (open) {
      snapshotRef.current = collection;
      setName(collection.name);
      setIndustry(collection.industry);
      setRegion(collection.region);
      setDescription(collection.description ?? '');
      setErrors({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const next: FieldErrors = {};
    if (!name.trim()) next.name = 'Name is required.';
    if (!industry.trim()) next.industry = 'Industry is required.';
    if (!region.trim()) next.region = 'Region is required.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    // Diff-aware payload — only include fields that actually changed
    // since the modal opened.
    const snapshot = snapshotRef.current;
    const payload: UpdateCollectionPayload = {};
    const trimmedName = name.trim();
    const trimmedIndustry = industry.trim();
    const trimmedRegion = region.trim();
    const nextDescription = description.trim() ? description.trim() : null;
    const currentDescription = snapshot.description ?? null;
    if (trimmedName !== snapshot.name) payload.name = trimmedName;
    if (trimmedIndustry !== snapshot.industry) payload.industry = trimmedIndustry;
    if (trimmedRegion !== snapshot.region) payload.region = trimmedRegion;
    if (nextDescription !== currentDescription) payload.description = nextDescription;

    if (Object.keys(payload).length === 0) {
      // Nothing changed — just close.
      onOpenChange(false);
      return;
    }

    update.mutate(payload, {
      onSuccess: () => onOpenChange(false),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Edit collection details</DialogTitle>
          <DialogDescription>
            Update the collection's name, industry, region, or description. Taxonomy,
            dimensions, and channels can't be edited here.
          </DialogDescription>
        </DialogHeader>

        <form id="edit-collection-form" onSubmit={handleSubmit} className="space-y-4 py-2">
          <div>
            <Label htmlFor="edit-collection-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-collection-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((er) => ({ ...er, name: undefined }));
              }}
              className="mt-1"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="edit-collection-industry">
                Industry <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-collection-industry"
                value={industry}
                onChange={(e) => {
                  setIndustry(e.target.value);
                  setErrors((er) => ({ ...er, industry: undefined }));
                }}
                className="mt-1"
              />
              {errors.industry && (
                <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
              )}
            </div>
            <div>
              <Label htmlFor="edit-collection-region">
                Region <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-collection-region"
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setErrors((er) => ({ ...er, region: undefined }));
                }}
                className="mt-1"
              />
              {errors.region && (
                <p className="mt-1 text-sm text-red-600">{errors.region}</p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="edit-collection-description">Description</Label>
            <Textarea
              id="edit-collection-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-collection-form"
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCollectionModal;
